import type {
  ConnectedPosition,
  OverlayConfig,
  OverlayRef,
  PositionStrategy,
} from '@angular/cdk/overlay';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ComponentPortal } from '@angular/cdk/portal';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import {
  ComponentRef,
  Injectable,
  ChangeDetectorRef,
  inject,
  ViewContainerRef,
} from '@angular/core';

import { Subject } from 'rxjs';

import type { OverlayPosition } from '@core/prosemirror-legacy';

export interface WysiwygPositionStrategy extends PositionStrategy {
  setOrigin: ({ x, y }: { x: number; y: number }) => void;
}

@Injectable()
export class WysiwygComponentPortalService<C> {
  protected componentRef: ComponentRef<C> | null;
  protected overlayRef: OverlayRef | null;
  protected positionStrategy: WysiwygPositionStrategy | null;
  protected ngOnDestroy$ = new Subject<void>();
  protected closeComponents$ = new Subject<void>();
  protected overlayConfig: OverlayConfig = {};
  protected positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  protected portalRef: ComponentPortal<C> | null;

  protected vcRef = inject(ViewContainerRef);
  protected _cdRef = inject(ChangeDetectorRef);
  protected _viewportRuler = inject(ViewportRuler);
  protected _document = inject(DOCUMENT);
  protected _platform = inject(Platform);

  constructor(
    private _overlayContainer: OverlayContainer,
    protected overlay: Overlay,
  ) {}

  public ngOnDestroy(): void {
    this.close();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public close(): void {
    this.closeComponents$.next();
    this.componentRef?.destroy();
    this.componentRef = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.positionStrategy?.dispose();
    this.positionStrategy = null;
    this._cdRef.detectChanges();
  }

  public updatePosition(position: { left: number; top: number }): void {
    if (!this.overlayRef || !this.positionStrategy) return;

    this.positionStrategy.setOrigin({
      x: position.left,
      y: position.top,
    });
    this.overlayRef.updatePosition();
  }

  public isOpen(): boolean {
    return !!this.componentRef;
  }

  public openComponent(
    component: new (...args: any[]) => C,
    position: OverlayPosition | DOMRect,
    preferredPositions: ConnectedPosition[],
    _options?: { skipComponentCD: boolean },
  ) {
    const width =
      (<DOMRect>position).width ?? (position.right ? position.right - position.left : 0);
    const height =
      (<DOMRect>position).height ?? (position.bottom ? position.bottom - position.top : 0);

    const connectedTo = {
      x: position.left,
      y: position.top,
      width,
      height,
    };
    const positionStrategy = new FlexibleConnectedPositionStrategy(
      connectedTo,
      this._viewportRuler,
      this._document,
      this._platform,
      this._overlayContainer,
    );

    positionStrategy.withGrowAfterOpen(true);
    positionStrategy.withPush(false);
    this.overlayRef = this.overlay.create({
      positionStrategy,
      ...this.overlayConfig,
    });
    positionStrategy.withPositions([...preferredPositions, ...this.positions]);
    this.positionStrategy = positionStrategy;
    this.portalRef = new ComponentPortal(component, this.vcRef);
    this.componentRef = this.overlayRef.attach(this.portalRef);
    if (!_options?.skipComponentCD) {
      this.componentRef.changeDetectorRef.detectChanges();
    }
    this._cdRef.detectChanges();
  }
}
